import {Options} from '@splidejs/splide';
import subscribe from 'callbag-subscribe';
import pipe from 'callbag-pipe';

import {ShopifyProductInfoService} from '../../api/services/shopify-product_info.service';
import {SphereItem} from '../../sphere_item';
import {IdealOverlayView} from './ideal-product_info.view';
import {IdealVariant, ShopifyProduct} from '../../interfaces/shopify-product.interface';
import {ProductSlides} from '../../product_slides';
import {InputEventUtils} from '../../utils/input_event_utils';
import {Metrics} from '../../metrics';
import {MATOMO_EVENT_NAMES} from '../../metric-events';
import {ProductFields} from '../shopify-product-info/components/product-fields/product-fields_component';
import {ShopifyButtons} from '../shopify-product-info/components/buttons/shopify-buttons_component';
import {VariantsComponent} from '../shopify-product-info/components/variants/variants_component';
import {EcommerceOverlaySettings} from '../../interfaces/planogram.interface';
import {AppUtils} from '../../utils/app_utils';
import {FontLoader} from '../../font_loader';
import {L10nUtils} from '../../utils/l10n_utils';
import {CurrencyService} from '../../api/services/currency.service';
import {CurrencyUtils} from '../../utils/currency_utils';
import {AppState} from '../../shared/app.state';
import {ProductInfoBase} from '../product-info/product-info-base_overlay';
import {PRODUCT_GALLERY_ITEM} from '../../shared/constants';
import {shopifyOverlayHandler} from '../../custom_event_utils';
import {ShoppingCartWidgetComponent} from '../../shopping-cart-widget/shopping-cart-widget.component';
import {IdealCartService} from '../../api/services/ideal-cart.service';
import {IdealService} from '../../api/services/ideal.service';
import {IdealQuantityPicker} from './components/quantity-picker/quantity-picker';
import {Planogram} from '../../planogram';
import {ProductOverlayOptions} from '../../overlay';

export class IdealProductInfo extends ProductInfoBase {
  protected readonly shopifyService: IdealService;
  product: ShopifyProduct;
  protected productFields: ProductFields;
  protected quantityPicker: IdealQuantityPicker;
  protected buttons: ShopifyButtons;
  protected overlaySettings: EcommerceOverlaySettings;
  protected quantityVideoSlides: number;
  protected readonly blurInformationBound: typeof this.blurInformation;
  protected shoppingCartWidget: ShoppingCartWidgetComponent;
  openPersonalizedOverlay: Function;
  showShoppingCartWidget: boolean = false;
  variant: IdealVariant;
  protected variantComponents: Array<VariantsComponent>;

  constructor(
    container: HTMLElement,
    item: SphereItem,
    protected planogram: Planogram,
    options: ProductOverlayOptions,
    productInfoService: ShopifyProductInfoService,
    protected shoppingCartService: IdealCartService,
    protected currencyService: CurrencyService,
    closeCallback: (changeUrl: boolean) => void
  ) {
    super(container, item, options, productInfoService, closeCallback);
    this.shopifyService = new IdealService();
    this.quantityVideoSlides = 0;
    this.blurInformationBound = this.blurInformation.bind(this);

    pipe(
      this.shopifyService.selectedProductSubject,
      subscribe({
        next: (variant: IdealVariant) => {
          this.updateProductPrice(variant);
          this.getPreSelectSlide(variant);
          this.variant = variant;
        }
      })
    );

    pipe(
      this.shoppingCartService.processingSubject,
      subscribe({
        next: (loading: boolean) => {
          if (
            !loading &&
            this.shoppingCartWidget &&
            !this.shoppingCartWidget?.panelVisibility &&
            this.showShoppingCartWidget
          ) {
            this.shoppingCartWidget.panelVisibility = true;
          }
        }
      })
    );

    shopifyOverlayHandler.listen('shoppingCartVisibility', this.blurInformationBound);
  }

  get enablePersonalizeOverlay() {
    return false;
  }

  protected async init(): Promise<void> {
    this.overlaySettings = this.item.planogram.ecommerceOverlaySettingsPdp;
    await super.init();
    this.shopifyService.updateProductData(this.product);
  }

  private getPreSelectSlide = variant => {
    if (!variant?.image_id) {
      return;
    }
    const imgVariantIndex =
      this.quantityVideoSlides +
      this.product?.product_images.findIndex(
        img => img?.shopify_id === variant.image_id || img?.id === variant.image_id
      );
    const currentSlideIndex = this.gallerySplide.index;

    if (imgVariantIndex >= 0 && imgVariantIndex !== currentSlideIndex) {
      this.gallerySplide.go(imgVariantIndex);
    }
  };

  protected setItemContent() {
    this.container.innerHTML = IdealOverlayView(this.product);
    this.mainContainer = this.container.querySelector('.ideal-template');
    if (this.overlaySettings.enable_buttons) {
      this.variantComponents = this.product.options
        .sort((prevOption, nextOption) => prevOption.position - nextOption.position)
        .map(option => {
          return new VariantsComponent(option, this.product, this.mainContainer, this.shopifyService);
        });
      this.quantityPicker = new IdealQuantityPicker(
        this.product,
        this.mainContainer,
        this.shopifyService,
        this.shoppingCartService
      );
      this.buttons = new ShopifyButtons(
        this,
        this.product,
        this.overlaySettings,
        this.mainContainer,
        this.quantityPicker,
        this.shopifyService,
        this.shoppingCartService
      );
      this.shoppingCartWidget = new ShoppingCartWidgetComponent(
        this.planogram,
        this.shoppingCartService,
        this.currencyService,
        null,
        this.quantityPicker,
        'app-ecommerce-cart-container',
        this.product
      );
    }
    this.productFields = new ProductFields(this.product, this.mainContainer, this.overlaySettings);
    this.setOverlayFonts();
    this.syncEcommerceOverlayColor();
  }

  protected initImageSlider() {
    super.initImageSlider();
    if (this.product.video_first) {
      this.quantityVideoSlides = this.productSlides.slides.reduce((acc, item) => {
        return item.type === PRODUCT_GALLERY_ITEM.VIDEO ? ++acc : acc;
      }, 0);
    }
  }

  protected setOverlayFonts() {
    this.overlaySettings?.ecommerce_fonts.forEach(font => {
      const fontSettings = {...font, assignment: `ecommerce-overlay-${font.assignment}`};
      FontLoader.mountCustomFont(fontSettings);
    });
  }

  protected syncEcommerceOverlayColor() {
    const root = document.documentElement;
    if (this.overlaySettings?.background_color) {
      const ecommerceOverlayColorRgb = AppUtils.hex2rgb(this.overlaySettings.background_color);

      root.style.setProperty(`--ecommerce-overlay-primary-color`, `${this.overlaySettings.background_color}`);
      root.style.setProperty(`--ecommerce-overlay-primary-color-rgb`, `${ecommerceOverlayColorRgb}`);
    }

    if (this.overlaySettings?.secondary_color) {
      const ecommerceOverlayColorRgb = AppUtils.hex2rgb(this.overlaySettings.secondary_color);

      root.style.setProperty(`--ecommerce-overlay-secondary-color`, `${this.overlaySettings.secondary_color}`);
      root.style.setProperty(`--ecommerce-overlay-secondary-color-rgb`, `${ecommerceOverlayColorRgb}`);
    }
  }

  protected initProductPrice() {}

  protected initDescriptionControls() {}

  protected initGalleryViewControls() {}

  protected initOverlayElements() {}

  initPurchasingFlow() {}

  setCustomButtons(buttons) {}

  protected initOverlayControls() {
    const closeButton: HTMLElement = this.mainContainer.querySelector<HTMLElement>(
      '#ideal-product-details-container .overlay-button-close'
    );

    if (closeButton) {
      InputEventUtils.addSelectEvents(closeButton, e => {
        Metrics.storeTheEvent(
          this.item.planogram.name,
          'click',
          MATOMO_EVENT_NAMES.CLICK_ECOMMERCE_OVERLAY_CLOSE_BUTTON(this.productIdentifier, this.productName)
        );
        this.closeOverlay(e);
      });
    } else {
      console.error('element not found - ".overlay-button-close"');
    }
  }

  private blurInformation(options) {
    options.value
      ? this.mainContainer.classList.add('is-cart-active')
      : this.mainContainer.classList.remove('is-cart-active');
  }

  private getStockValue(variant: IdealVariant) {
    const priceWrapper = this.mainContainer.querySelector<HTMLElement>('.app-ecommerce-product-price');
    const stockValueContainer = priceWrapper.querySelector<HTMLElement>('.app-ecommerce-product-stock-value');
    let stockValue: string;

    if (variant?.isAutoSelected) {
      stockValueContainer.classList.add('is-hidden');
    } else {
      stockValueContainer.classList.remove('is-hidden');
    }

    switch (true) {
      case variant?.quantity > 0 && variant?.quantity < 10: {
        stockValue = `${L10nUtils.l10n('shopify-product-overlay.product-stock-n')}: ${variant?.quantity}`;
        break;
      }

      case variant?.quantity >= 10: {
        stockValue = L10nUtils.l10n('shopify-product-overlay.product-stock-available');
        break;
      }

      default: {
        stockValue = L10nUtils.l10n('shopify-product-overlay.product-out-of-stock');
      }
    }

    (stockValueContainer as HTMLElement).innerText = stockValue;
  }

  protected updateProductPrice(selectedVariant) {
    if (!this.mainContainer) {
      return;
    }
    const priceWrapper = this.mainContainer.querySelector<HTMLElement>('.app-ecommerce-product-price');
    const priceContainer = priceWrapper.querySelector<HTMLElement>('.app-ecommerce-product-price-value');
    const discountPriceContainer = priceWrapper.querySelector<HTMLElement>(
      '.app-ecommerce-product-discount-price-value'
    );
    priceWrapper.classList.remove('has-discount-price', 'is-unavailable', 'is-unavailable-without-margin');

    this.getStockValue(selectedVariant);

    if (!selectedVariant && !selectedVariant.isAutoSelected) {
      priceWrapper.classList.add('is-unavailable-without-margin');
      (priceContainer as HTMLElement).innerText = '';
      (discountPriceContainer as HTMLElement).innerText = '';
      return;
    } else if (selectedVariant.quantity <= 0 && !selectedVariant.isAutoSelected) {
      priceWrapper?.classList.add('is-unavailable');
    }

    const {selectedCurrencyCode, currenciesList} = this.currencyService;
    const getPriceValue = price => CurrencyUtils.getPriceWithSymbolOrCode(price, selectedCurrencyCode, currenciesList);

    if (selectedVariant?.compare_at_price && parseFloat(selectedVariant?.compare_at_price)) {
      // due to weird behaviour of shopify api discount price logic
      (priceContainer as HTMLElement).innerText = getPriceValue(selectedVariant.compare_at_price);
      (discountPriceContainer as HTMLElement).innerText = getPriceValue(selectedVariant.price);
      priceWrapper.classList.add('has-discount-price');
    } else {
      (priceContainer as HTMLElement).innerText = getPriceValue(selectedVariant.price);
      (discountPriceContainer as HTMLElement).innerText = '';
    }
  }

  protected handleImageMatomoEvents(e: Event) {
    const target = e.target as HTMLElement;
    if (target.closest('.splide__slide')) {
      let name = '';
      let imgElement: HTMLElement;

      if (target.dataset.slideType === 'li') {
        imgElement = target.querySelector<HTMLElement>('img');
      } else if (target.dataset.slideType === 'img') {
        imgElement = target;
      }

      if (imgElement) {
        name = imgElement.getAttribute('data-name');
      }

      Metrics.storeTheEvent(
        AppState.planogramName,
        'click',
        `${MATOMO_EVENT_NAMES.CLICK_ECOMMERCE_OVERLAY_PRODUCT_IMAGE(this.product?.identifier)}${name ? '-' + name : ''}`
      );
    }
  }

  protected handleThumbnailMatomoEvents(e: Event) {
    const target = e.target as HTMLElement;
    if (target.closest('.splide__slide')) {
      const thumbnailImage = target.closest('.splide__slide').querySelector<HTMLElement>('img');
      const thumbnailType = thumbnailImage.getAttribute('data-type');

      Metrics.storeTheEvent(
        AppState.planogramName,
        'click',
        MATOMO_EVENT_NAMES.CLICK_ECOMMERCE_PRODUCT_THUMBNAIL(thumbnailType, this.productIdentifier, this.productName)
      );
    }
  }

  protected getGalleryCarouselConfig(productSlides: ProductSlides): Options {
    return {
      type: 'loop',
      perPage: 1,
      pagination: false,
      keyboard: true,
      speed: 100,
      updateOnMove: true,
      lazyLoad: 'nearby',
      // @ts-ignore
      video: {
        playerOptions: {
          youtube: {
            playlist: undefined
          }
        }
      }
    };
  }

  protected getThumbsCarouselConfig(productSlides: ProductSlides): Options {
    return {
      autoWidth: true,
      autoHeight: true,
      gap: '1.53vw',
      speed: 150,
      rewind: true,
      pagination: false,
      isNavigation: true,
      lazyLoad: 'sequential',
      perPage: Math.min(productSlides.length - 1, 5),
      focus: 4,
      perMove: 1,
      keyboard: false,
      breakpoints: {
        1200: {
          gap: 18
        },
        1024: {
          gap: 16
        }
      }
    };
  }

  validateVariants() {
    return this.variantComponents.every(variantComponent => variantComponent?.validate());
  }

  dispose() {
    // TODO: Fix unsubscribe from listeners in splide
    //  (splide video extension not able to destroy with invalid youtube video in slide)
    try {
      this.gallerySplide?.destroy(true);
      this.thumbnailSplide?.destroy(true);
    } catch (e) {
      console.error('Gallery destroy error:', e);
    }
    this.shoppingCartWidget?.dispose();
    this.quantityPicker?.dispose();
  }
}
